import _ from 'lodash';
import styled from 'styled-components';
import React, { useEffect } from 'react';
import Link from '@material-ui/core/Link';
import { verticalScroller } from 'components/base/scroller';
import { Flex, Padding, Separator } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { getColor } from 'common/constants/_colors';
import { SPACES } from 'common/constants/sizes';
import Icon from 'components/base/Icon';
import * as LighthouseService from 'common/services/lighthouse.service';
import { ARTICLE_IDS, TRACKING } from 'common/constants/constants';
import { calculatePercentage } from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import {
  getActivePeopleTitle,
  getOrderedPeopleEffort,
  getContributorAvatarAndName
} from '../services/projects.services';
import PeopleEffortSummary from './PeopleEffortSummary';

const Scroll = styled.div`
  ${verticalScroller()};
  max-height: 28.2rem;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const ExcludedScroll = styled.div`
  ${verticalScroller()};
  max-height: 11.5rem;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const EffortProgressBar = styled.div`
  ${({ alpha }) =>
    `background-color: ${getColor('havelockBlue', alpha ? alpha : 1)};`};
  width: 100%;
  height: 4px;
  line-height: 4px;
  border-radius: 10px;
`;

const ExcludedWrapper = styled(Flex)`
  opacity: 0.4;
  filter: grayscale(1);
`;

const AverageContributionIterationsView = ({ percent, effort }) => (
  <Flex direction='row' items='center' justify='left'>
    <Text
      size='large'
      align='left'
      pr={SPACES.small}
      width='2rem'
      ellipsisAfter='4rem'
    >
      {effort}
    </Text>
    <Flex minWidth='8rem'>
      <EffortProgressBar alpha='0.1'>
        <EffortProgressBar style={{ width: `${percent}%` }} />
      </EffortProgressBar>
    </Flex>
  </Flex>
);
const FTEContributionIterationsView = ({ effort }) => (
  <Flex direction='row' items='center' justify='flex-start'>
    <Text size='large' pr={SPACES.small}>
      {effort}
    </Text>
    <Text color='shuttleGray'>{effort > 1 ? 'Iterations' : 'Iteration'}</Text>
  </Flex>
);

const PeopleEffortTooltip = ({
  uniquePeopleEffort = [],
  uniqueExcludedPeople = [],
  projectConfig,
  project,
  selectedPeriod,
  isSingleIterationView,
  iterationsLength = 0,
  singleIteration,
  isFTECalculation
}) => {
  const { icon } = projectConfig;

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.HOVER, {
      page: TRACKING.PAGES.PROJECTS,
      hover_type: TRACKING.HOVER.ACTIVE_PEOPLE,
      project_name: _.get(project, 'name', '')
    });
  }, []);

  const activeUniquePeople = getOrderedPeopleEffort(uniquePeopleEffort);

  const excludedUniquePeople = getOrderedPeopleEffort(uniqueExcludedPeople);

  const peopleDataRow = (name, id, effort) => {
    const percent = calculatePercentage(effort, iterationsLength);

    return (
      <Flex items='center' pb='small' key={id}>
        {getContributorAvatarAndName(id, name, '65%')}
        {!isSingleIterationView ? (
          <>
            {isFTECalculation ? (
              <FTEContributionIterationsView effort={effort} />
            ) : (
              <AverageContributionIterationsView
                percent={percent}
                effort={effort}
              />
            )}
          </>
        ) : null}
      </Flex>
    );
  };

  return (
    <Padding padding='small'>
      <Flex items='center' pb='medium'>
        <Icon name={icon} color='silverSand' />
        <Text size='medium' pl='0.7rem'>
          Active people -{' '}
          {getActivePeopleTitle(
            selectedPeriod,
            iterationsLength,
            isSingleIterationView,
            singleIteration
          )}
        </Text>
      </Flex>
      <Flex direction='column' pb='small'>
        <PeopleEffortSummary
          projectConfig={projectConfig}
          project={project}
          shouldShowAvgPerDuration={false}
        />
      </Flex>
      <Separator height='1px' width='100%' margin='1rem 0' />
      <Flex direction='column'>
        <Flex pb='normal'>
          <Flex width='74%'>
            <Text size='small' color='shuttleGray' textTransform='uppercase'>
              {activeUniquePeople.length} people on this project
            </Text>
          </Flex>
          {!isSingleIterationView ? (
            <Flex self='center'>
              <Text
                size='small'
                color='shuttleGray'
                textTransform='uppercase'
                align='left'
              >
                Active in:
              </Text>
            </Flex>
          ) : null}
        </Flex>
        <Scroll>
          {activeUniquePeople.map(({ name, id, effort }) => {
            return peopleDataRow(name, id, effort);
          })}
        </Scroll>
      </Flex>
      {excludedUniquePeople.length ? (
        <>
          <Separator height='1px' width='100%' margin='1.5rem 0' />
          <Flex direction='column'>
            <Text
              size='small'
              color='shuttleGray'
              pb={SPACES.normal}
              textTransform='uppercase'
            >
              {uniqueExcludedPeople.length} excluded from project data
            </Text>
            <ExcludedWrapper>
              <ExcludedScroll>
                {excludedUniquePeople.map(({ name, id, effort }) =>
                  peopleDataRow(name, id, effort)
                )}
              </ExcludedScroll>
            </ExcludedWrapper>
          </Flex>
        </>
      ) : null}
      <Flex direction='row' items='center' pt='medium'>
        <Link
          onClick={() =>
            LighthouseService.navigateArticle(ARTICLE_IDS.WHO_IS_ACTIVE)
          }
        >
          how do we know who is active?
        </Link>
      </Flex>
    </Padding>
  );
};

export default PeopleEffortTooltip;
